import React, { useEffect, useState, useCallback } from "react";
import cn from "classnames";
import { createPortal } from "react-dom";
import styles from "./Toast.module.sass";
import Icon from "../../Icon";

const Toast = ({ 
    message, 
    onClose, 
    duration = 5000,
    type = "message", // message, success, error, info
    action,
    position = "bottom-right" 
}) => {
    const [isVisible, setIsVisible] = useState(true);
    const [isExiting, setIsExiting] = useState(false);
    
    const handleClose = useCallback(() => {
        setIsExiting(true);
        setTimeout(() => {
            setIsVisible(false);
            if (onClose) onClose();
        }, 300); // Match animation duration
    }, [onClose]);
    
    useEffect(() => {
        const timer = setTimeout(() => {
            handleClose();
        }, duration);
        
        return () => clearTimeout(timer);
    }, [duration, handleClose]);
    
    if (!isVisible) return null;
    
    return createPortal(
        <div 
            className={cn(
                styles.toast,
                styles[position],
                styles[type],
                { [styles.exiting]: isExiting }
            )}
        >
            {message.avatar && (
                <img 
                    src={message.avatar} 
                    alt={message.sender}
                    className={styles.avatar}
                />
            )}
            
            <div className={styles.content}>
                {message.sender && (
                    <div className={styles.sender}>{message.sender}</div>
                )}
                <div className={styles.text}>
                    {message.text || message}
                </div>
            </div>
            
            {action && (
                <button 
                    className={styles.actionBtn}
                    onClick={action.onClick}
                >
                    {action.label}
                </button>
            )}
            
            <button 
                className={styles.closeBtn}
                onClick={handleClose}
            >
                <Icon name="close" size="16" />
            </button>
        </div>,
        document.body
    );
};

// Toast Manager to handle multiple toasts
export const ToastManager = () => {
    const [toasts, setToasts] = useState([]);
    
    useEffect(() => {
        // Listen for custom toast events
        const handleShowToast = (event) => {
            const { message, type, action, duration } = event.detail;
            showToast(message, type, action, duration);
        };
        
        window.addEventListener('show-toast', handleShowToast);
        return () => window.removeEventListener('show-toast', handleShowToast);
    }, []);
    
    const showToast = (message, type = "message", action = null, duration = 5000) => {
        const id = Date.now();
        const newToast = { id, message, type, action, duration };
        setToasts(prev => [...prev, newToast]);
    };
    
    const removeToast = (id) => {
        setToasts(prev => prev.filter(toast => toast.id !== id));
    };
    
    return createPortal(
        <div className={styles.toastContainer}>
            {toasts.map((toast, index) => (
                <div 
                    key={toast.id}
                    style={{ 
                        transform: `translateY(-${index * 80}px)`,
                        transition: 'transform 0.3s ease'
                    }}
                >
                    <Toast
                        message={toast.message}
                        type={toast.type}
                        action={toast.action}
                        duration={toast.duration}
                        onClose={() => removeToast(toast.id)}
                    />
                </div>
            ))}
        </div>,
        document.body
    );
};

// Helper function to show toast
export const showToast = (message, type = "message", action = null, duration = 5000) => {
    const event = new CustomEvent('show-toast', {
        detail: { message, type, action, duration }
    });
    window.dispatchEvent(event);
};

export default Toast;
